import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Select from "react-select";
import { FiUser, FiMail, FiMapPin, FiClock, FiEdit3, FiCheck, FiAlertTriangle } from "react-icons/fi";
import { Spinner, Modal, Button } from "react-bootstrap";

function Profile({
  isDarkModeProp,
  onUserIdFetch,
}) {
  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [location, setLocation] = useState("");
  const [locationData, setLocationData] = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [zoomeyName, setZoomeyName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const searchTimeoutRef = useRef(null);
  const locationInputRef = useRef(null);
  const navigate = useNavigate();

  // Timezone options for react-select
  const timezoneOptions = moment.tz.names().map((tz) => ({
    value: tz,
    label: `${tz} (UTC${moment.tz(tz).format("Z")})`,
  }));

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    async function fetchUser() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        setUser(session.user);
        onUserIdFetch && onUserIdFetch(session.user.id, session.user.email);
        const { data: profile } = await supabase
          .from("profiles")
          .select("name, username, location, time_zone, nickname, country, state, city")
          .eq("id", session.user.id)
          .single();
        
        console.log("loader off");
        setName(profile?.name || "");
        setUsername(profile?.username || "");
        setTimezone(profile?.time_zone || "");
        setZoomeyName(profile?.nickname || "");
        
        // Set location display
        if (profile?.location) {
          setLocation(profile.location);
          setLocationData({
            city: profile?.city || "",
            state: profile?.state || "",
            country: profile?.country || "",
            display_name: profile.location,
          });
        }
      } else {
        navigate("/login");
      }
    }
    fetchUser();
  }, [navigate]);

  // Search location using OpenStreetMap Nominatim (free, no API key)
  const searchLocation = async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      const data = await response.json();
      
      const suggestions = data.map((item) => ({
        display_name: item.display_name,
        city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || "",
        state: item.address?.state || item.address?.region || "",
        country: item.address?.country || "",
        lat: item.lat,
        lon: item.lon,
      }));
      
      setLocationSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (err) {
      console.error("[Profile] Location search error:", err);
      setLocationSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    setLocationData(null);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 300);
  };

  const handleSelectLocation = (suggestion) => {
    // Build a clean display name
    const parts = [];
    if (suggestion.city) parts.push(suggestion.city);
    if (suggestion.state) parts.push(suggestion.state);
    if (suggestion.country) parts.push(suggestion.country);
    const displayName = parts.join(", ") || suggestion.display_name;
    
    setLocation(displayName);
    setLocationData({
      city: suggestion.city,
      state: suggestion.state,
      country: suggestion.country,
      display_name: displayName,
    });
    setShowSuggestions(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      setError("Session expired. Please login again.");
      setTimeout(() => setError(""), 2000);
      setIsUpdating(false);
      return;
    }

    const { error } = await supabase.from("profiles").upsert({
      id: user.id,
      name,
      username,
      country: locationData?.country || null,
      state: locationData?.state || null,
      city: locationData?.city || null,
      location: locationData?.display_name || location || null,
      time_zone: timezone,
      nickname: zoomeyName,
    });

    if (error) {
      setError(error.message);
      setTimeout(() => setError(""), 2000);
      setIsUpdating(false);
    } else {
      try {
        await fetch(
          `${process.env.REACT_APP_ZOOMEY_API_URL}user_profile_update`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              user_id: user.id,
              nickname: zoomeyName,
            }),
          }
        );
      } catch (err) {
        console.error("Profile update API failed:", err);
      }
      setSuccess(true);
      setIsUpdating(false);
      setTimeout(() => setSuccess(false), 2000);
    }
  };

  const inputStyle = {
    backgroundColor: "var(--input-box-bg)",
    border: "1px solid var(--border-color)",
    borderRadius: "12px",
    padding: "12px 16px 12px 44px",
    color: "var(--text)",
    fontSize: "14px",
    transition: "all 0.2s ease",
    width: "100%",
  };

  const iconStyle = {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--teal-bg)",
    fontSize: "18px",
  };

  const labelStyle = {
    color: "var(--text)",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "10px",
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    opacity: 0.8,
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail.trim().toLowerCase() !== user?.email?.toLowerCase()) {
      setDeleteError("Email does not match. Please enter your account email.");
      return;
    }
    setIsDeleting(true);
    setDeleteError("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setDeleteError("Session expired. Please login again.");
        setIsDeleting(false);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_ZOOMEY_API_URL || ""}/api/user/delete`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        setDeleteError(data.error || "Failed to deactivate account. Please try again.");
        setIsDeleting(false);
        return;
      }
      await supabase.auth.signOut();
      navigate("/login");
    } catch (err) {
      console.error("[Profile] Delete account error:", err);
      setDeleteError("Something went wrong. Please try again.");
      setIsDeleting(false);
    }
  };

  // Get initials from name
  const getInitials = () => {
    if (name) {
      const nameParts = name.trim().split(" ").filter(part => part.length > 0);
      if (nameParts.length >= 2) {
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
      }
      return nameParts[0]?.charAt(0).toUpperCase() || "";
    }
    return user?.email?.charAt(0).toUpperCase() || "U";
  };

  return (
    <div
      style={{
        padding: "24px",
        color: "var(--text)",
        backgroundColor: "var(--background)",
        minHeight: "100%",
        borderRadius: "16px",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "32px",
          paddingBottom: "24px",
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        <div
          style={{
            width: "72px",
            height: "72px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--teal-bg), var(--orange-bg))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            fontWeight: "700",
            color: "var(--textwhite)",
            boxShadow: "0 4px 20px rgba(0, 150, 137, 0.3)",
            letterSpacing: "1px",
          }}
        >
          {getInitials()}
        </div>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "4px" }}>
            {name || "Your Profile"}
          </h2>
          <p style={{ opacity: 0.6, fontSize: "13px", margin: 0, color: "var(--teal-bg)" }}>
            {user?.email || ""}
          </p>
          <p style={{ opacity: 0.7, fontSize: "12px", margin: "4px 0 0 0" }}>
            Keep Zoomey aligned with your workflow and timezone.
          </p>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div
          style={{
            background: "rgba(231, 0, 11, 0.1)",
            border: "1px solid var(--danger)",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "20px",
            color: "var(--danger)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <i className="bi bi-exclamation-circle-fill"></i>
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            background: "rgba(0, 150, 137, 0.1)",
            border: "1px solid var(--teal-bg)",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "20px",
            color: "var(--teal-bg)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FiCheck size={18} />
          Profile updated — you are all set.
        </div>
      )}

      <form onSubmit={handleUpdateProfile}>
        <div className="row g-4">
          {/* Email Field */}
          <div className="col-md-6">
            <label style={labelStyle}>Email Address</label>
            <div style={{ position: "relative" }}>
              <FiMail style={iconStyle} />
              <input
                type="email"
                className="form-control"
                value={user?.email || ""}
                readOnly
                style={{
                  ...inputStyle,
                  opacity: 0.7,
                  cursor: "not-allowed",
                }}
              />
            </div>
          </div>

          {/* Name Field */}
          <div className="col-md-6">
            <label style={labelStyle}>Full Name</label>
            <div style={{ position: "relative" }}>
              <FiUser style={iconStyle} />
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="How should your profile name appear?"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Username Field */}
          <div className="col-md-6">
            <label style={labelStyle}>Username</label>
            <div style={{ position: "relative" }}>
              <span style={{ ...iconStyle, fontSize: "16px" }}>@</span>
              <input
                type="text"
                className="form-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Choose your username"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Zoomey Name Field */}
          <div className="col-md-6">
            <label style={labelStyle}>How should Zoomey address you?</label>
            <div style={{ position: "relative" }}>
              <FiEdit3 style={iconStyle} />
              <input
                type="text"
                className="form-control"
                value={zoomeyName}
                onChange={(e) => setZoomeyName(e.target.value)}
                placeholder="Name used in assistant replies"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Location Field */}
          <div className="col-md-6">
            <label style={labelStyle}>Location</label>
            <div style={{ position: "relative" }} ref={locationInputRef}>
              <FiMapPin style={iconStyle} />
              <input
                type="text"
                className="form-control"
                value={location}
                onChange={handleLocationInputChange}
                onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search your city or work base..."
                style={inputStyle}
              />
              {isSearching && (
                <div style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}>
                  <Spinner animation="border" size="sm" style={{ color: "var(--teal-bg)" }} />
                </div>
              )}
              
              {showSuggestions && locationSuggestions.length > 0 && (
                <ul style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "var(--input-box-bg)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  listStyle: "none",
                  padding: "8px 0",
                  margin: "4px 0 0 0",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 1000,
                }}>
                  {locationSuggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectLocation(suggestion)}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "10px 16px",
                        cursor: "pointer",
                        color: "var(--text)",
                        fontSize: "13px",
                        transition: "background-color 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--teal-bg)";
                        e.currentTarget.style.color = "var(--textwhite)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "var(--text)";
                      }}
                    >
                      <span>📍</span>
                      <span style={{ lineHeight: 1.4 }}>{suggestion.display_name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Timezone Field */}
          <div className="col-md-6">
            <label style={labelStyle}>Timezone</label>
            <div style={{ position: "relative" }}>
              <FiClock
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--teal-bg)",
                  fontSize: "18px",
                  zIndex: 1,
                }}
              />
              <Select
                id="time_zone"
                value={
                  timezoneOptions.find((option) => option.value === timezone) ||
                  null
                }
                onChange={(selectedOption) =>
                  setTimezone(selectedOption?.value || "")
                }
                options={timezoneOptions}
                isClearable
                isSearchable
                placeholder="Select timezone..."
                styles={{
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: "var(--input-box-bg)",
                    borderColor: state.isFocused ? "var(--teal-bg)" : "var(--border-color)",
                    borderRadius: "12px",
                    minHeight: "48px",
                    paddingLeft: "32px",
                    color: "var(--text)",
                    fontSize: "14px",
                    boxShadow: state.isFocused
                      ? "0 0 0 3px rgba(0, 150, 137, 0.15)"
                      : "none",
                    "&:hover": {
                      borderColor: "var(--teal-bg)",
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: "var(--input-box-bg)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused
                      ? "var(--teal-bg)"
                      : "var(--input-box-bg)",
                    color: state.isFocused ? "var(--textwhite)" : "var(--text)",
                    cursor: "pointer",
                    borderRadius: "8px",
                    margin: "4px 8px",
                    width: "calc(100% - 16px)",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "var(--text)",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "var(--text)",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "var(--text)",
                    opacity: 0.5,
                  }),
                }}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isUpdating}
          style={{
            background: isUpdating
              ? "var(--teal-bg)"
              : "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
            color: "var(--textwhite)",
            border: "none",
            borderRadius: "12px",
            padding: "14px 40px",
            fontSize: "15px",
            fontWeight: 600,
            opacity: isUpdating ? 0.7 : 1,
            cursor: isUpdating ? "not-allowed" : "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: "32px",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 15px rgba(0, 150, 137, 0.3)",
          }}
          onMouseEnter={(e) => {
            if (!isUpdating) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 150, 137, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 150, 137, 0.3)";
          }}
        >
          {isUpdating && (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {isUpdating ? "Updating..." : "Save profile preferences"}
        </button>
      </form>

      {/* Danger Zone */}
      <div style={{ marginTop: "40px", paddingTop: "28px", borderTop: "1px solid var(--border-color)" }}>
        <div
          style={{
            background: "rgba(220, 53, 69, 0.06)",
            border: "1px solid rgba(220, 53, 69, 0.25)",
            borderRadius: "14px",
            padding: "22px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <FiAlertTriangle size={16} style={{ color: "#dc3545" }} />
            <span style={{ fontWeight: 700, color: "#dc3545", fontSize: "14px" }}>Danger Zone</span>
          </div>
          <p style={{ fontSize: "13px", color: "var(--text)", opacity: 0.65, marginBottom: "16px" }}>
            Deactivate your account. You will not be able to log in again.
          </p>
          <button
            type="button"
            onClick={() => { setDeleteConfirmEmail(""); setDeleteError(""); setShowDeleteModal(true); }}
            style={{
              background: "transparent",
              color: "#dc3545",
              border: "1px solid rgba(220, 53, 69, 0.6)",
              borderRadius: "10px",
              padding: "9px 20px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#dc3545"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#dc3545"; }}
          >
            Delete my account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => !isDeleting && setShowDeleteModal(false)}
        centered
        backdrop={isDeleting ? "static" : true}
      >
        <Modal.Header
          closeButton={!isDeleting}
          style={{ background: "var(--background)", borderBottom: "1px solid var(--border-color)" }}
        >
          <Modal.Title style={{ color: "#dc3545", fontSize: "15px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
            <FiAlertTriangle /> Delete Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--background)", color: "var(--text)" }}>
          <p style={{ fontSize: "14px", marginBottom: "6px" }}>
            Your account will be <strong>permanently deactivated</strong>. You will not be able to log in again.
          </p>
          <p style={{ fontSize: "13px", opacity: 0.65, marginBottom: "18px" }}>
            To confirm, type your email: <strong style={{ color: "var(--teal-bg)" }}>{user?.email}</strong>
          </p>
          <input
            type="email"
            value={deleteConfirmEmail}
            onChange={(e) => { setDeleteConfirmEmail(e.target.value); setDeleteError(""); }}
            placeholder="Enter your email address"
            disabled={isDeleting}
            style={{
              width: "100%",
              backgroundColor: "var(--input-box-bg)",
              border: `1px solid ${deleteError ? "#dc3545" : "var(--border-color)"}`,
              borderRadius: "10px",
              padding: "11px 14px",
              color: "var(--text)",
              fontSize: "14px",
              outline: "none",
            }}
          />
          {deleteError && (
            <p style={{ color: "#dc3545", fontSize: "12px", marginTop: "6px", marginBottom: 0 }}>
              {deleteError}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "var(--background)", borderTop: "1px solid var(--border-color)" }}>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
            style={{ borderRadius: "10px", fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={isDeleting || !deleteConfirmEmail.trim()}
            style={{
              background: "#dc3545",
              border: "none",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              opacity: isDeleting || !deleteConfirmEmail.trim() ? 0.65 : 1,
            }}
          >
            {isDeleting ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Deactivating...</>
            ) : (
              "Permanently delete account"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Profile;
